/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import React from "react"
import dayjs from "dayjs"
import { jsx, css } from "@emotion/core"
import { Dropdown, Button } from "@duxton-web/ui"
import {
  MoreHorizontalUniversal,
  RealtimeFeedbackOutlined,
} from "@duxton-web/icons"
import { MenuProps } from "@duxton-web/ui/es/menu"
import { Review, ReviewUnReportableReason } from "types/api/menuFeedback.type"
import { ActionType } from "types/models/feedback"
import { useDispatch } from "react-redux"
import { actionCreator } from "store/store"
import { isEmpty } from "lodash"
import { useTranslationsMap } from "common/utils/i18n"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"

import { canSubmitReport } from "pages/feedback/utils"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import { ReviewThrottleResponse } from "store/duck/feedback.type"

type ReviewActionColumnProp = {
  review: Review
  onClick(actionType: ActionType, review: Review): void
  reviewThrottle?: ReviewThrottleResponse
}

export enum ReviewAction {
  REPORT = "REPORT",
}

const reportStyle = css({
  padding: "12px !important",
  display: "flex",
  gap: "8px",
  alignItems: "center",
  width: "250px",
})

const ReviewActionColumn: React.FC<ReviewActionColumnProp> = ({
  review,
  onClick,
  reviewThrottle,
}) => {
  const dispatch = useDispatch()

  const textMap = useTranslationsMap({
    report: "feedback.cta.report",
    reviewReported: "feedback.helperText.reported",
  })

  const isReported =
    review.unreportableReason ===
    ReviewUnReportableReason.ReviewUnreportableReasonReviewIsReported

  const enableMerchantReviewThrottle = getGrabXValueFromLocalStorage<boolean>(
    MerchantPortalFeatureFlag.enableMerchantReviewThrottle,
  )
  // Check if the reviewThrottle value is already available for specific merchantID in the state and not using old day value.
  const existingValue =
    reviewThrottle &&
    reviewThrottle.merchantID === review.merchantID &&
    reviewThrottle.fetchHour === dayjs().hour()

  const handleMenuClick = ({ key }: any) => {
    switch (key) {
      case ReviewAction.REPORT:
        // Calling new endpoint to check either the merchant is allowing to report or not
        if (enableMerchantReviewThrottle && review && !existingValue) {
          dispatch(
            actionCreator.feedbackDrawer.postMerchantReviewThrottle(
              review.merchantID,
            ),
          )
        }
        TroyWebTracker.sendEvent({
          category: EventCategory.feedbackHomePage,
          action: EventAction.clickReport,
          labels: {
            REVIEW_ID: review?.reviewID,
            REVIEW_RATING: review?.rating,
          },
        })
        if (isReported) {
          onClick(ActionType.SEE_DETAILS, review)
          break
        }
        dispatch(
          actionCreator.feedbackDrawer.toggleReport({
            review,
            visible: true,
          }),
        )
        break
      default:
    }
  }

  const isDisabled = !canSubmitReport(review)
  const actions: MenuProps["items"] = [
    {
      label: (
        <span
          css={reportStyle}
          style={{ color: isDisabled ? "inherit" : "#f55538" }}
        >
          {" "}
          <RealtimeFeedbackOutlined
            style={{ color: "#f55538", fontSize: 18 }}
          />{" "}
          {textMap.report}{" "}
        </span>
      ),
      key: ReviewAction.REPORT,
      disabled: isDisabled,
    },
  ]

  return (
    <div onClick={e => e?.stopPropagation()}>
      {!isEmpty(actions) && (
        <Dropdown menu={{ items: actions, onClick: handleMenuClick }}>
          <Button shape="default">
            <MoreHorizontalUniversal />
          </Button>
        </Dropdown>
      )}
    </div>
  )
}

export default React.memo(ReviewActionColumn)
